import { useRef, useEffect, useState } from 'react';
import { X, Play, Pause, SkipBack, SkipForward, Rewind, FastForward } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';

export default function PersistentPlayer() {
  const { currentTrack, stopTrack } = usePlayer();
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSoundTitle, setCurrentSoundTitle] = useState(null);

  useEffect(() => {
    // Initialize SoundCloud Widget when track changes
    if (currentTrack && iframeRef.current && window.SC) {
      const widget = window.SC.Widget(iframeRef.current);
      widgetRef.current = widget;
      setCurrentSoundTitle(null);

      widget.bind(window.SC.Widget.Events.READY, () => {
        widget.play();
        setIsPlaying(true);
        widget.getDuration((d) => setDuration(d));
      });

      widget.bind(window.SC.Widget.Events.PLAY, () => {
        setIsPlaying(true);
        // Get title of current sound (track within a set)
        widget.getCurrentSound((sound) => {
          if (sound && sound.title) setCurrentSoundTitle(sound.title);
        });
      });

      widget.bind(window.SC.Widget.Events.PAUSE, () => setIsPlaying(false));

      widget.bind(window.SC.Widget.Events.PLAY_PROGRESS, (data) => {
        setProgress(data.relativePosition);
        setCurrentTime(data.currentPosition);
      });

      widget.bind(window.SC.Widget.Events.FINISH, () => setIsPlaying(false));
    }
  }, [currentTrack]);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!widgetRef.current) return;
    if (isPlaying) {
      widgetRef.current.pause();
    } else {
      widgetRef.current.play();
    }
  };

  const handlePrev = () => {
    if (!widgetRef.current) return;
    widgetRef.current.prev();
  };

  const handleNext = () => {
    if (!widgetRef.current) return;
    widgetRef.current.next();
  };

  const handleSeekBack = () => {
    if (!widgetRef.current) return;
    widgetRef.current.getPosition((pos) => {
      widgetRef.current.seekTo(Math.max(0, pos - 15000));
    });
  };

  const handleSeekForward = () => {
    if (!widgetRef.current) return;
    widgetRef.current.getPosition((pos) => {
      widgetRef.current.seekTo(Math.min(duration, pos + 15000));
    });
  };

  const handleProgressClick = (e) => {
    if (!widgetRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickRatio = (e.clientX - rect.left) / rect.width;
    widgetRef.current.seekTo(clickRatio * duration);
  };

  const handleClose = () => {
    if (widgetRef.current) {
      widgetRef.current.pause();
    }
    stopTrack();
  };

  if (!currentTrack) return null;

  const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(currentTrack.url)}&color=%23003478&auto_play=true&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false`;

  // Check if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div
      data-testid="persistent-player"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(5, 10, 20, 0.95)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(0, 180, 255, 0.1)',
        zIndex: 1000,
        padding: isMobile ? '12px 16px 16px' : '12px 20px',
        minHeight: isMobile ? '80px' : '72px',
      }}
    >
      {/* Top row: track info + controls */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? '12px' : '16px',
        marginBottom: '10px',
      }}>
        {/* Cover */}
        {currentTrack.coverUrl && (
          <img
            src={currentTrack.coverUrl}
            alt={currentTrack.title}
            style={{
              width: isMobile ? '40px' : '44px',
              height: isMobile ? '40px' : '44px',
              objectFit: 'cover',
              borderRadius: '4px',
              flexShrink: 0,
            }}
          />
        )}

        {/* Track info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Artist name - smaller, caption style */}
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '10px' : '11px',
            letterSpacing: '0.08em',
            color: 'var(--vvs-cyan)',
            margin: '0 0 2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {currentTrack.artist || 'fromnow'}
          </p>
          {/* Track title // Project name - larger, title style */}
          <p style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '12px' : '13px',
            color: '#F0F4F8',
            margin: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {currentSoundTitle ? `${currentSoundTitle} // ${currentTrack.title}` : currentTrack.title}
          </p>
        </div>

        {/* Controls - simplified on mobile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px' }}>
          {/* Prev - hide on mobile */}
          {!isMobile && (
            <button
              onClick={handlePrev}
              style={controlButtonStyle}
              data-testid="player-prev"
              title="Previous"
            >
              <SkipBack size={16} />
            </button>
          )}

          {/* -15s - hide on mobile */}
          {!isMobile && (
            <button
              onClick={handleSeekBack}
              style={controlButtonStyle}
              data-testid="player-rewind"
              title="-15s"
            >
              <Rewind size={16} />
            </button>
          )}

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            style={{
              ...controlButtonStyle,
              background: 'rgba(0, 180, 255, 0.15)',
              width: isMobile ? '48px' : '40px',
              height: isMobile ? '48px' : '40px',
              minWidth: '48px',
              minHeight: '48px',
            }}
            data-testid="player-play-pause"
          >
            {isPlaying ? <Pause size={isMobile ? 20 : 18} /> : <Play size={isMobile ? 20 : 18} style={{ marginLeft: '2px' }} />}
          </button>

          {/* +15s - hide on mobile */}
          {!isMobile && (
            <button
              onClick={handleSeekForward}
              style={controlButtonStyle}
              data-testid="player-forward"
              title="+15s"
            >
              <FastForward size={16} />
            </button>
          )}

          {/* Next */}
          <button
            onClick={handleNext}
            style={{
              ...controlButtonStyle,
              minWidth: '44px',
              minHeight: '44px',
            }}
            data-testid="player-next"
            title="Next"
          >
            <SkipForward size={16} />
          </button>

          {/* Close */}
          <button
            onClick={handleClose}
            style={{
              ...controlButtonStyle,
              marginLeft: isMobile ? '4px' : '8px',
              color: 'var(--vvs-muted)',
              minWidth: '44px',
              minHeight: '44px',
            }}
            data-testid="player-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Progress bar - larger touch target on mobile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ 
          fontFamily: 'Space Grotesk', 
          fontSize: '10px', 
          color: 'var(--vvs-muted)',
          minWidth: '36px',
        }}>
          {formatTime(currentTime)}
        </span>
        
        <div 
          onClick={handleProgressClick}
          style={{
            flex: 1,
            height: isMobile ? '8px' : '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: isMobile ? '4px' : '2px',
            cursor: 'pointer',
            position: 'relative',
            touchAction: 'none',
          }}
          data-testid="player-progress"
        >
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${progress * 100}%`,
            background: 'linear-gradient(90deg, rgba(0, 160, 200, 0.8), rgba(0, 200, 255, 0.9))',
            borderRadius: isMobile ? '4px' : '2px',
            transition: 'width 0.1s linear',
          }} />
        </div>
        
        <span style={{ 
          fontFamily: 'Space Grotesk', 
          fontSize: '10px', 
          color: 'var(--vvs-muted)',
          minWidth: '36px',
          textAlign: 'right',
        }}>
          {formatTime(duration)}
        </span>
      </div>

      {/* Hidden iframe for audio */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        width="1"
        height="1"
        frameBorder="0"
        allow="autoplay"
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
        title="SoundCloud Player"
      />
    </div>
  );
}

const controlButtonStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--vvs-cyan)',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  transition: 'all 0.2s',
  width: '36px',
  height: '36px',
  minWidth: '44px',
  minHeight: '44px',
};
